import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VideoBanner from './VideoBanner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ── icons ───────────────────────────────────────────── */
const Icon = ({ children, size = 21 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    {children}
  </svg>
);
const BackIcon = () => (<Icon size={18}><path d="M15 5l-7 7 7 7" /></Icon>);
const GalleryIcon = () => (
  <Icon size={18}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M3 16l5-4 4 3 3-2 6 5" />
  </Icon>
);
const WaveIcon = () => (<Icon size={18}><path d="M4 12h2M8 8v8M12 5v14M16 8v8M20 12h0" /></Icon>);
const FilmIcon = () => (
  <Icon size={18}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" />
  </Icon>
);
const PlusIcon = () => (<Icon size={18}><path d="M12 5v14M5 12h14" /></Icon>);
const ChevronIcon = () => (<Icon size={16}><path d="M6 9l6 6 6-6" /></Icon>);
const XIcon = () => (<Icon size={14}><path d="M6 6l12 12M18 6L6 18" /></Icon>);

/* ── cost estimate ───────────────────────────────────────
   uses the selected model's "from" per-second price × duration.
   that's the lowest-resolution floor; higher res costs more, and
   the real charge always lands in the library after generation. */
const usd = (n) => `$${Number(n).toFixed(3)}`;

const RESOLUTIONS = [
  { id: '480p',  label: '480p'  },
  { id: '720p',  label: '720p'  },
  { id: '1080p', label: '1080p' },
];
const RATIOS = [
  { id: '16:9', label: 'landscape' },
  { id: '9:16', label: 'portrait'  },
  { id: '1:1',  label: 'square'    },
];
const FALLBACK_DURATIONS = [4, 6, 8, 10];

export default function VideoHome() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [prompt, setPrompt] = useState('');
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('bytedance/seedance-2.0');
  const [modelOpen, setModelOpen] = useState(false);
  const [resId, setResId] = useState('720p');
  const [ratioId, setRatioId] = useState('16:9');
  const [duration, setDuration] = useState(6);
  const [audio, setAudio] = useState(false);

  // image references
  const [firstFrame, setFirstFrame] = useState(null);   // {url} or null (marked slot)
  const [lastFrame, setLastFrame] = useState(null);     // {url} or null (marked slot)
  const [refImages, setRefImages] = useState([]);       // [{url}] — as many as you want
  const [uploading, setUploading] = useState(null);     // 'first' | 'last' | 'refs'

  const [status, setStatus] = useState('idle');
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const modelRef = useRef(null);
  const refsInputRef = useRef(null);

  // fetch the model catalog (already alphabetical from the backend)
  useEffect(() => {
    axios.get(`${API}/video/models?token=${token}`)
      .then(res => {
        setModels(res.data.models || []);
        if (res.data.default) setModelId(res.data.default);
      })
      .catch(err => console.log(err));

    // if the user tapped "regenerate" in the library, prefill from that
    try {
      const raw = localStorage.getItem('videoRegenerate');
      if (raw) {
        const r = JSON.parse(raw);
        if (r.prompt) setPrompt(r.prompt);
        if (r.model) setModelId(r.model);
        if (r.resolution) setResId(r.resolution);
        if (r.aspect_ratio) setRatioId(r.aspect_ratio);
        if (r.duration) setDuration(r.duration);
        if (typeof r.generate_audio === 'boolean') setAudio(r.generate_audio);
        if (r.first_frame_url) setFirstFrame({ url: r.first_frame_url });
        if (r.last_frame_url) setLastFrame({ url: r.last_frame_url });
        if (Array.isArray(r.input_reference_urls) && r.input_reference_urls.length) {
          setRefImages(r.input_reference_urls.map(u => ({ url: u })));
        }
        localStorage.removeItem('videoRegenerate');
      }
    } catch (e) { /* ignore bad payloads */ }

    const onClick = (e) => {
      if (modelRef.current && !modelRef.current.contains(e.target)) setModelOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const selected = models.find(m => m.id === modelId);
  const durations = (selected && selected.durations && selected.durations.length)
    ? selected.durations
    : FALLBACK_DURATIONS;

  // if the current length isn't valid for the newly-picked model, snap to a
  // sensible one (prefer 6s, else the closest available)
  useEffect(() => {
    if (!durations.includes(duration)) {
      const next = durations.includes(6)
        ? 6
        : durations.reduce((a, b) => (Math.abs(b - duration) < Math.abs(a - duration) ? b : a));
      setDuration(next);
    }
  }, [modelId, durations]); // eslint-disable-line react-hooks/exhaustive-deps

  const estimate = useMemo(() => {
    if (!selected) return null;
    return selected.from_price * duration;
  }, [selected, duration]);

  const MAX_REFS = 9; // safe cap; Seedance 2.0 allows 9, 2.5 allows more

  // upload a single file to a marked frame slot (first / last)
  const uploadFrame = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/video/reference?token=${token}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const val = { url: res.data.url };
      if (slot === 'first') setFirstFrame(val);
      else if (slot === 'last') setLastFrame(val);
    } catch (e) {
      console.log(e);
      setError('couldn’t upload that image — try another');
    } finally {
      setUploading(null);
    }
  };

  // upload one or more files into the free-form reference gallery
  const uploadRefs = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_REFS - refImages.length;
    if (room <= 0) return;
    setUploading('refs');
    try {
      for (const file of files.slice(0, room)) {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/video/reference?token=${token}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setRefImages(prev => [...prev, { url: res.data.url }]);
      }
    } catch (e) {
      console.log(e);
      setError('couldn’t upload one of those — try again');
    } finally {
      setUploading(null);
    }
  };

  const removeRef = (idx) => setRefImages(prev => prev.filter((_, i) => i !== idx));

  const poll = (videoId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/video/${videoId}?token=${token}`);
        const v = res.data.video;
        if (v.status === 'completed') {
          clearInterval(pollRef.current);
          setVideoUrl(v.video_url);
          setStatus('done');
        } else if (v.status === 'failed') {
          clearInterval(pollRef.current);
          setError(v.error || 'generation failed — try again');
          setStatus('failed');
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setError('lost connection while generating');
        setStatus('failed');
      }
    }, 4000);
  };

  const generate = async () => {
    if (!prompt.trim() || status === 'generating') return;
    setStatus('generating');
    setError(null);
    setVideoUrl(null);
    try {
      const res = await axios.post(`${API}/video/generate`, {
        token,
        prompt: prompt.trim(),
        model: modelId,
        resolution: resId,
        aspect_ratio: ratioId,
        duration,
        generate_audio: audio,
        first_frame_url: firstFrame?.url || null,
        last_frame_url: lastFrame?.url || null,
        input_reference_urls: refImages.map(r => r.url),
      });
      poll(res.data.video.id);
    } catch (e) {
      console.log(e);
      setError('couldn’t start the job — try again in a sec');
      setStatus('failed');
    }
  };

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: '#e8ddd0', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
    },
    header: {
      padding: '20px 28px', display: 'flex', alignItems: 'center', gap: '14px',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', flexShrink: 0, zIndex: 50,
    },
    backBtn: {
      width: '34px', height: '34px', borderRadius: '50%', background: 'transparent',
      border: '1px solid rgba(201,147,90,0.2)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#c9935a', flexShrink: 0,
    },
    logo: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', fontWeight: 300,
      fontStyle: 'italic', color: '#e8ddd0', letterSpacing: '0.04em', flex: 1,
    },
    logoSpan: { color: '#c9935a' },
    galleryBtn: {
      padding: '8px 14px', borderRadius: '20px', border: '1px solid rgba(201,147,90,0.2)',
      background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      display: 'flex', alignItems: 'center', gap: '7px', flexShrink: 0,
    },
    body: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '32px 24px 80px', maxWidth: '680px', width: '100%',
      margin: '0 auto', boxSizing: 'border-box',
    },
    greeting: { marginBottom: '30px' },
    greetingTop: { fontSize: '13px', color: '#4a3d30', letterSpacing: '0.06em', marginBottom: '4px' },
    greetingName: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '30px',
      fontWeight: 300, fontStyle: 'italic', color: '#e8ddd0',
    },
    sectionTitle: {
      fontSize: '11px', letterSpacing: '0.16em', color: '#4a3d30',
      textTransform: 'uppercase', marginBottom: '14px',
    },
    card: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '18px', padding: '18px', marginBottom: '18px', boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', minHeight: '96px', resize: 'vertical', background: '#0e0b0f',
      border: '1px solid rgba(201,147,90,0.12)', borderRadius: '14px', padding: '14px 16px',
      color: '#e8ddd0', fontSize: '15px', fontFamily: "'DM Sans', sans-serif",
      fontWeight: 300, lineHeight: 1.6, boxSizing: 'border-box', outline: 'none',
    },
    fieldLabel: {
      fontSize: '11px', letterSpacing: '0.1em', color: '#4a3d30',
      textTransform: 'uppercase', marginBottom: '10px',
    },
    fieldGroup: { marginBottom: '18px' },
    pillRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    pill: (active) => ({
      padding: '8px 16px', borderRadius: '20px', fontSize: '12px', letterSpacing: '0.04em',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, cursor: 'pointer',
      color: active ? '#c9935a' : '#8a7a6a',
      background: active ? 'rgba(201,147,90,0.1)' : 'transparent',
      border: active ? '1px solid rgba(201,147,90,0.32)' : '1px solid rgba(201,147,90,0.1)',
    }),

    /* model dropdown */
    modelWrap: { position: 'relative' },
    modelBtn: {
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '13px 16px', borderRadius: '14px', background: '#0e0b0f',
      border: '1px solid rgba(201,147,90,0.12)', color: '#e8ddd0', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: '14px', boxSizing: 'border-box',
    },
    modelBtnPrice: { fontSize: '11px', color: '#8a7a6a', marginLeft: '8px' },
    modelMenu: {
      position: 'absolute', top: '54px', left: 0, right: 0, zIndex: 60,
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '14px',
      padding: '6px', maxHeight: '280px', overflowY: 'auto',
      boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
    },
    modelItem: (active) => ({
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '11px 13px', borderRadius: '10px', cursor: 'pointer', fontSize: '13.5px',
      color: active ? '#c9935a' : '#e8ddd0',
      background: active ? 'rgba(201,147,90,0.08)' : 'transparent',
    }),
    modelItemPrice: { fontSize: '11px', color: '#8a7a6a', flexShrink: 0, marginLeft: '10px' },

    /* reference tiles */
    refGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' },
    frameRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
    refGallery: {
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px',
    },
    galleryTile: {
      position: 'relative', aspectRatio: '1 / 1', borderRadius: '12px',
      border: '1px dashed rgba(201,147,90,0.18)', background: '#0e0b0f',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: '4px', cursor: 'pointer', color: '#8a7a6a', overflow: 'hidden',
    },
    refTile: {
      position: 'relative', aspectRatio: '1 / 1', borderRadius: '14px',
      border: '1px dashed rgba(201,147,90,0.18)', background: '#0e0b0f',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: '6px', cursor: 'pointer', color: '#8a7a6a', overflow: 'hidden',
    },
    refTileImg: { width: '100%', height: '100%', objectFit: 'cover' },
    refTileLabel: { fontSize: '10.5px', letterSpacing: '0.04em', textAlign: 'center', padding: '0 4px' },
    refRemove: {
      position: 'absolute', top: '6px', right: '6px', width: '22px', height: '22px',
      borderRadius: '50%', background: 'rgba(14,11,15,0.8)', border: '1px solid rgba(201,147,90,0.3)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c9935a', cursor: 'pointer',
    },
    hiddenInput: { display: 'none' },
    refNote: { fontSize: '11px', color: '#4a3d30', marginTop: '12px', lineHeight: 1.5 },

    /* audio toggle */
    toggleRow: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', borderRadius: '14px',
      border: '1px solid rgba(201,147,90,0.12)', background: '#0e0b0f',
    },
    toggleLeft: { display: 'flex', alignItems: 'center', gap: '12px', color: '#c9935a' },
    toggleText: { display: 'flex', flexDirection: 'column', gap: '2px' },
    toggleTitle: { fontSize: '14px', color: '#e8ddd0', fontWeight: 300 },
    toggleSub: { fontSize: '11px', color: '#4a3d30' },
    switch: (on) => ({
      width: '46px', height: '26px', borderRadius: '20px', flexShrink: 0,
      background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'rgba(201,147,90,0.12)',
      border: '1px solid rgba(201,147,90,0.2)', cursor: 'pointer', position: 'relative',
      transition: 'background .18s ease',
    }),
    knob: (on) => ({
      position: 'absolute', top: '2px', left: on ? '22px' : '2px',
      width: '20px', height: '20px', borderRadius: '50%',
      background: on ? '#0e0b0f' : '#8a7a6a', transition: 'left .18s ease',
    }),

    costStrip: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 18px', borderRadius: '14px',
      background: 'linear-gradient(135deg, rgba(58,42,26,0.4), rgba(90,61,32,0.25))',
      border: '1px solid rgba(201,147,90,0.18)', marginBottom: '18px',
    },
    costLabel: { fontSize: '11px', letterSpacing: '0.1em', color: '#8a7a6a', textTransform: 'uppercase' },
    costValue: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '26px',
      fontWeight: 400, fontStyle: 'italic', color: '#c9935a',
    },
    costSub: { fontSize: '11px', color: '#4a3d30', marginTop: '2px' },

    genBtn: (disabled) => ({
      width: '100%', padding: '16px', borderRadius: '16px', border: 'none',
      background: disabled ? 'rgba(201,147,90,0.15)' : 'linear-gradient(135deg, #8a5f35, #c9935a)',
      color: disabled ? '#8a7a6a' : '#0e0b0f', fontSize: '14px', letterSpacing: '0.08em',
      textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", fontWeight: 400,
      cursor: disabled ? 'default' : 'pointer',
    }),

    video: { width: '100%', borderRadius: '14px', display: 'block' },
    statusBox: { padding: '28px', textAlign: 'center', fontSize: '13px', color: '#8a7a6a' },
    errorText: { color: '#c9935a', fontSize: '13px', textAlign: 'center', padding: '20px' },

    soonRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px' },
    soonChip: {
      display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px',
      borderRadius: '14px', border: '1px dashed rgba(201,147,90,0.14)', color: '#8a7a6a',
      fontSize: '12px', flex: '1 1 auto', justifyContent: 'center',
    },
    soonPill: {
      fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#c9935a',
      border: '1px solid rgba(201,147,90,0.2)', borderRadius: '20px', padding: '2px 7px',
    },
  };

  const PROMPT_MAX = 4096;
  const promptLen = prompt.length;
  const promptOver = promptLen > PROMPT_MAX;
  const canGenerate = prompt.trim().length > 0 && !promptOver && status !== 'generating' && !uploading;

  // a single marked frame slot (first / last)
  const FrameTile = ({ slot, value, setValue, label }) => {
    const inputRef = useRef(null);
    return (
      <div
        style={s.refTile}
        onClick={() => { if (!value && uploading !== slot) inputRef.current?.click(); }}
        role="button" tabIndex={0}
      >
        {value ? (
          <>
            <img src={value.url} alt={label} style={s.refTileImg} />
            <div
              style={s.refRemove}
              onClick={(e) => { e.stopPropagation(); setValue(null); }}
              role="button" tabIndex={0}
            >
              <XIcon />
            </div>
          </>
        ) : uploading === slot ? (
          <div style={s.refTileLabel}>uploading…</div>
        ) : (
          <>
            <PlusIcon />
            <div style={s.refTileLabel}>{label}</div>
          </>
        )}
        <input
          ref={inputRef} type="file" accept="image/*" style={s.hiddenInput}
          onChange={(e) => uploadFrame(slot, e.target.files?.[0])}
        />
      </div>
    );
  };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />
      <style>{`textarea::placeholder { color: #4a3d30; }`}</style>

      <header style={s.header}>
        <div style={s.backBtn} onClick={() => navigate('/companions')} role="button" tabIndex={0}>
          <BackIcon />
        </div>
        <div style={s.logo}>re<span style={s.logoSpan}>v</span>erie</div>
        <div style={s.galleryBtn} onClick={() => navigate('/video/library')} role="button" tabIndex={0}>
          <GalleryIcon /> library
        </div>
      </header>

      <div style={s.body}>
        <div style={s.greeting}>
          <div style={s.greetingTop}>video</div>
          <div style={s.greetingName}>dream something ♡</div>
        </div>

        <div style={s.card}>
          {/* MODEL PICKER */}
          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>model</div>
            <div style={s.modelWrap} ref={modelRef}>
              <div style={s.modelBtn} onClick={() => setModelOpen(o => !o)} role="button" tabIndex={0}>
                <span>{selected ? selected.label : 'choose a model'}</span>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {selected && <span style={s.modelBtnPrice}>from {usd(selected.from_price)}/s</span>}
                  <span style={{ color: '#8a7a6a', marginLeft: '6px' }}><ChevronIcon /></span>
                </span>
              </div>
              {modelOpen && (
                <div style={s.modelMenu}>
                  {models.map(m => (
                    <div
                      key={m.id} style={s.modelItem(m.id === modelId)}
                      onClick={() => { setModelId(m.id); setModelOpen(false); }}
                      role="button" tabIndex={0}
                    >
                      <span>{m.label}</span>
                      <span style={s.modelItemPrice}>from {usd(m.from_price)}/s</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>your prompt</div>
            <textarea
              style={{
                ...s.textarea,
                ...(promptOver ? { borderColor: 'rgba(220,80,80,0.5)' } : {}),
              }}
              placeholder="a slow cinematic shot of..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div style={{
              display: 'flex', justifyContent: 'flex-end', marginTop: '6px',
              fontSize: '11px', color: promptOver ? '#d05050' : '#4a3d30',
            }}>
              {promptLen} / {PROMPT_MAX}
            </div>
          </div>

          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>resolution</div>
            <div style={s.pillRow}>
              {RESOLUTIONS.map(r => (
                <div key={r.id} style={s.pill(resId === r.id)} onClick={() => setResId(r.id)} role="button" tabIndex={0}>
                  {r.label}
                </div>
              ))}
            </div>
          </div>

          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>aspect ratio</div>
            <div style={s.pillRow}>
              {RATIOS.map(r => (
                <div key={r.id} style={s.pill(ratioId === r.id)} onClick={() => setRatioId(r.id)} role="button" tabIndex={0}>
                  {r.label}
                </div>
              ))}
            </div>
          </div>

          <div style={s.fieldGroup}>
            <div style={s.fieldLabel}>length</div>
            <div style={s.pillRow}>
              {durations.map(d => (
                <div key={d} style={s.pill(duration === d)} onClick={() => setDuration(d)} role="button" tabIndex={0}>
                  {d}s
                </div>
              ))}
            </div>
          </div>

          {/* AUDIO TOGGLE */}
          <div style={{ ...s.fieldGroup, marginBottom: 0 }}>
            <div style={s.fieldLabel}>audio</div>
            <div style={s.toggleRow}>
              <div style={s.toggleLeft}>
                <WaveIcon />
                <div style={s.toggleText}>
                  <div style={s.toggleTitle}>generate audio</div>
                  <div style={s.toggleSub}>let the AI add its own sound</div>
                </div>
              </div>
              <div style={s.switch(audio)} onClick={() => setAudio(a => !a)} role="switch" aria-checked={audio} tabIndex={0}>
                <div style={s.knob(audio)} />
              </div>
            </div>
          </div>
        </div>

        {/* IMAGE REFERENCES */}
        <div style={s.sectionTitle}>image references</div>
        <div style={s.card}>
          {/* marked frame slots — exact first/last frame control */}
          <div style={s.fieldLabel}>frames (optional)</div>
          <div style={s.frameRow}>
            <FrameTile slot="first" value={firstFrame} setValue={setFirstFrame} label="first frame" />
            <FrameTile slot="last"  value={lastFrame}  setValue={setLastFrame}  label="last frame" />
          </div>

          {/* free-form gallery — as many style/character refs as you want */}
          <div style={{ ...s.fieldLabel, marginTop: '18px' }}>
            reference images ({refImages.length}/{MAX_REFS})
          </div>
          <div style={s.refGallery}>
            {refImages.map((img, i) => (
              <div key={i} style={s.galleryTile}>
                <img src={img.url} alt={`ref ${i + 1}`} style={s.refTileImg} />
                <div
                  style={s.refRemove}
                  onClick={() => removeRef(i)}
                  role="button" tabIndex={0}
                >
                  <XIcon />
                </div>
              </div>
            ))}

            {refImages.length < MAX_REFS && (
              <div
                style={s.galleryTile}
                onClick={() => { if (uploading !== 'refs') refsInputRef.current?.click(); }}
                role="button" tabIndex={0}
              >
                {uploading === 'refs'
                  ? <div style={s.refTileLabel}>uploading…</div>
                  : <><PlusIcon /><div style={s.refTileLabel}>add</div></>}
              </div>
            )}
            <input
              ref={refsInputRef} type="file" accept="image/*" multiple style={s.hiddenInput}
              onChange={(e) => { uploadRefs(e.target.files); e.target.value = ''; }}
            />
          </div>

          <div style={s.refNote}>
            frames guide exact motion (image-to-video); reference images guide the
            look, characters & style. add several for consistency. all optional —
            support & limits vary by model.
          </div>

          {/* honest: video + audio refs aren't in OpenRouter's normalized API
              yet, they're model-specific. wiring next once we confirm fields. */}
          <div style={s.soonRow}>
            <div style={s.soonChip}><FilmIcon /> video ref <span style={s.soonPill}>soon</span></div>
            <div style={s.soonChip}><WaveIcon /> audio ref <span style={s.soonPill}>soon</span></div>
          </div>
        </div>

        {/* COST */}
        <div style={s.costStrip}>
          <div>
            <div style={s.costLabel}>estimated cost</div>
            <div style={s.costSub}>from {resId} · {ratioId} · {duration}s{audio ? ' · audio' : ''}</div>
          </div>
          <div style={s.costValue}>{estimate != null ? `~${usd(estimate)}` : '—'}</div>
        </div>

        <button style={s.genBtn(!canGenerate)} onClick={generate} disabled={!canGenerate}>
          {status === 'generating' ? 'generating…' : 'generate'}
        </button>

        {(status === 'generating' || status === 'done' || status === 'failed') && (
          <div style={{ ...s.card, marginTop: '18px' }}>
            {status === 'generating' && (
              <div style={s.statusBox}>weaving your clip together… this can take a few minutes 🌙</div>
            )}
            {status === 'failed' && <div style={s.errorText}>{error}</div>}
            {status === 'done' && videoUrl && (
              <video src={videoUrl} style={s.video} controls autoPlay loop playsInline />
            )}
          </div>
        )}
      </div>

      <VideoBanner />
    </div>
  );
}
